import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI } from '../../services/api';
import BrandLogo from './BrandLogo';
import { isPlatformAdmin } from '../../utils/auth';
import { Nav } from '../icons/NavIcon';
import ProductSidebarLink from './ProductSidebarLink';
import PulseScanIcon from '../icons/PulseScanIcon';
import UserProfileMenu from './UserProfileMenu';
import { useServiceActivity } from '../../hooks/useServiceActivity';

/** `gated: false` = always available (dashboard, sites list, install snippet). */
const navGroups = [
  {
    heading: 'Overview',
    items: [
      { path: '/dashboard', icon: Nav.home, label: 'Dashboard', gated: false },
      { path: '/inbox', icon: Nav.inbox, label: 'Inbox', gated: false, badgeKey: 'userInbox' },
    ],
  },
  {
    heading: 'Registered sites',
    items: [
      {
        path: '/projects',
        icon: Nav.layers,
        label: 'Sites & registration',
        gated: false,
        end: true,
      },
    ],
  },
  {
    heading: 'Experimentation',
    items: [
      { path: '/experiments', icon: Nav.cpu, label: 'A/B tests', service: 'ab_testing' },
      { path: '/click-path', icon: Nav.gitBranch, label: 'ClickPath', service: 'clickpath' },
    ],
  },
  // Analytics parent — the heading is the "Analytics" group. Each item
  // below is a child surface that lives under /analytics/*. Overview
  // (the existing /analytics page) is the parent's primary destination,
  // followed by Live activity, Funnels & journeys, PowerGrid, Heatmaps,
  // Sessions. Legacy URLs (/funnels, /heatmaps, /power-grid,
  // /session-replay, /analytics/olap) still resolve via redirects in
  // App.jsx.
  {
    heading: 'Analytics',
    items: [
      {
        path: '/analytics',
        icon: Nav.barChart2,
        label: 'Overview',
        anyOf: ['heatmaps', 'ab_testing', 'cwv', 'campaigns'],
        end: true,
      },
      {
        path: '/analytics/live',
        icon: Nav.radio,
        label: 'Live activity',
        anyOf: ['heatmaps', 'ab_testing', 'cwv', 'campaigns'],
        nested: true,
      },
      {
        path: '/analytics/funnels',
        icon: Nav.trendingUp,
        label: 'Funnels & journeys',
        anyOf: ['clickpath', 'heatmaps', 'ab_testing'],
        nested: true,
      },
      {
        path: '/analytics/powergrid',
        icon: Nav.grid,
        label: 'PowerGrid',
        anyOf: ['heatmaps', 'ab_testing', 'cwv'],
        nested: true,
      },
      {
        path: '/analytics/heatmaps',
        icon: Nav.map,
        label: 'Heatmaps',
        service: 'heatmaps',
        nested: true,
      },
      {
        path: '/analytics/sessions',
        icon: Nav.pieChart,
        label: 'Sessions',
        anyOf: ['heatmaps', 'clickpath'],
        service: 'heatmaps',
        nested: true,
      },
    ],
  },
  {
    heading: 'Growth',
    items: [
      { path: '/campaigns', icon: Nav.target, label: 'Campaigns', service: 'campaigns' },
      { path: '/seo-max', icon: Nav.search, label: 'SEOMAX', service: 'seomax' },
      {
        path: '/tracking',
        icon: Nav.activity,
        label: 'Tracking',
        anyOf: ['heatmaps', 'ab_testing'],
      },
    ],
  },
  {
    heading: 'Performance & reliability',
    items: [
      { path: '/core-web-vitals', icon: Nav.zap, label: 'Core Web Vitals', service: 'cwv' },
      { path: '/pulsescan/dashboard', icon: PulseScanIcon, label: 'PulseScan', service: 'cwv' },
      { path: '/pulsescan/queue', icon: Nav.list, label: 'PulseScan Jobs', service: 'cwv', nested: true },
      { path: '/pulsescan/page-insights', icon: Nav.fileText, label: 'Page Insights', service: 'cwv', nested: true },
    ],
  },
  {
    heading: 'Fleet & scale',
    items: [
      { path: '/swiss', icon: Nav.globe, label: 'Swiss (multisite)', service: 'swiss' },
      { path: '/operations', icon: Nav.inbox, label: 'Operations inbox', gated: false },
    ],
  },
  {
    heading: 'Setup',
    items: [{ path: '/snippet', icon: Nav.code, label: 'Install code', gated: false }],
  },
];

function accountItemsForUser(user) {
  const items = [
    { path: '/subscriptions', icon: Nav.creditCard, label: 'Subscriptions' },
    { path: '/settings', icon: Nav.settings, label: 'Settings' },
  ];
  if (isPlatformAdmin(user)) {
    items.push({ path: '/admin/dashboard', icon: Nav.pieChart, label: 'Admin console' });
  }
  return items;
}

export default function Sidebar({ isCollapsed = false, onToggleCollapse }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const activity = useServiceActivity();
  const [inboxUnread, setInboxUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await notificationsAPI.list({ limit: 1 });
        if (!cancelled) setInboxUnread(data.data?.unreadCount ?? 0);
      } catch {
        if (!cancelled) setInboxUnread(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /**
   * Resolve "is this nav item showing live activity?" from the activity map.
   * - `service: 'cwv'` → activity.cwv
   * - `anyOf: ['heatmaps', 'cwv', …]` → true if ANY of those services are running
   * Falls back to `false` for items without a service binding.
   */
  const isItemRunning = ({ service, anyOf }) => {
    if (anyOf?.length) return anyOf.some((k) => !!activity[k]);
    if (service) return !!activity[service];
    return false;
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-txnweb-canvas border-r border-black/[0.06] flex flex-col z-50 ${isCollapsed ? 'w-20' : 'w-64'}`}
      aria-label="Main navigation"
      data-tour-target="sidebar.nav"
    >
      <div className={`border-b border-black/[0.06] ${isCollapsed ? 'px-3 pt-3 pb-3' : 'px-4 pt-4 pb-3'}`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-2'}`}>
          {!isCollapsed && (
            <NavLink
              to="/dashboard"
              className="min-w-0 shrink focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0071E3]/35 rounded-md"
              aria-label="Dashboard home"
            >
              <BrandLogo variant="compact" />
            </NavLink>
          )}
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-black/[0.08] text-text-secondary hover:text-text-primary hover:bg-black/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0071E3]/35"
          >
            {isCollapsed ? <Nav.chevronsRight size={16} aria-hidden /> : <Nav.chevronsLeft size={16} aria-hidden />}
          </button>
        </div>
        {isCollapsed && (
          <div className="sr-only">
            <BrandLogo variant="compact" />
          </div>
        )}
      </div>

      <nav className={`txnweb-sidebar-nav flex-1 py-3 overflow-y-auto ${isCollapsed ? 'px-2' : 'px-3'}`}>
        {navGroups.map((group) => (
          <div key={group.heading} className="mb-4 last:mb-0">
            {!isCollapsed && <p className="sidebar-nav-heading">{group.heading}</p>}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const { path, icon: Icon, label, nested, gated, service, anyOf, end: endProp, badgeKey } = item;
                const unread = badgeKey === 'userInbox' ? inboxUnread : 0;
                if (gated === false) {
                  return (
                    <NavLink
                      key={path}
                      to={path}
                      className={({ isActive }) => {
                        const base = `${isActive ? 'sidebar-link-active' : 'sidebar-link'}${nested ? ' sidebar-link-nested' : ''}`;
                        return `${base} ${isCollapsed ? 'justify-center px-0' : ''}${!isCollapsed && unread > 0 ? ' pr-2' : ''}`;
                      }}
                      end={!!endProp}
                      title={isCollapsed ? (unread > 0 ? `${label} (${unread} unread)` : label) : undefined}
                      aria-label={unread > 0 ? `${label}, ${unread} unread` : label}
                    >
                      <span className="relative shrink-0">
                        <Icon size={nested ? 18 : 20} className="opacity-90" aria-hidden />
                        {isCollapsed && unread > 0 && (
                          <span
                            className="absolute -top-1 -right-1 min-w-[8px] h-2 rounded-full bg-rose-600 ring-2 ring-txnweb-canvas"
                            aria-hidden
                          />
                        )}
                      </span>
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 min-w-0 truncate">{label}</span>
                          {unread > 0 && (
                            <span className="badge badge-warning text-[11px] px-1.5 py-0 min-w-[1.25rem] justify-center">
                              {unread > 99 ? '99+' : unread}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                }
                return (
                  <ProductSidebarLink
                    key={path}
                    path={path}
                    label={label}
                    icon={Icon}
                    service={service}
                    anyOf={anyOf}
                    nested={nested}
                    end={endProp}
                    isCollapsed={isCollapsed}
                    running={isItemRunning(item)}
                  />
                );
              })}
            </div>
          </div>
        ))}
        <div className="mb-4 last:mb-0">
          {!isCollapsed && <p className="sidebar-nav-heading">Account</p>}
          <div className="space-y-0.5">
            {accountItemsForUser(user).map(({ path, icon: Icon, label }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) => {
                  const base = isActive ? 'sidebar-link-active' : 'sidebar-link';
                  return `${base} ${isCollapsed ? 'justify-center px-0' : ''}`;
                }}
                title={isCollapsed ? label : undefined}
                aria-label={label}
              >
                <Icon size={20} className="shrink-0 opacity-90" aria-hidden />
                {!isCollapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
      <div className={`border-t border-black/[0.06] ${isCollapsed ? 'p-3' : 'p-4'}`}>
        <UserProfileMenu
          settingsPath="/settings"
          subtitle={`${user?.subscription?.planDisplayName || user?.subscription?.plan || 'Free'} plan`}
          isCollapsed={isCollapsed}
          onLogout={handleLogout}
        />
      </div>
    </aside>
  );
}
